import { ClearFiltersIcon, SearchIcon } from '@/components/icons'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { MultiSelect, type MultiSelectOption } from '@/components/ui/multi-select'
import { cn } from '@/lib/utils'
import { NO_EPIC_FILTER_VALUE, WORK_ITEM_DEFAULT_SORT_DIRECTIONS, workItemStatusFilterOptions, type WorkItemSortColumn } from '@/types/workItem'
import { BoxSelect, X } from 'lucide-react'
import { useMemo } from 'react'

interface BacklogFiltersProps {
  search: string
  onSearchChange: (value: string) => void
  selectedEpics: string[]
  onEpicsChange: (values: string[]) => void
  epics: string[]
  statuses: string[]
  onStatusesChange: (statuses: string[]) => void
  assigneeOptions: MultiSelectOption[]
  assignedTo: string[]
  onAssignedToChange: (value: string[]) => void
  sortBy: WorkItemSortColumn
  sortDirection: 'asc' | 'desc'
  selectionMode: boolean
  onToggleSelectionMode: () => void
  onClearAll: () => void
}

export function BacklogFilters({
  search,
  onSearchChange,
  selectedEpics,
  onEpicsChange,
  epics,
  statuses,
  onStatusesChange,
  assigneeOptions,
  assignedTo,
  onAssignedToChange,
  sortBy,
  sortDirection,
  selectionMode,
  onToggleSelectionMode,
  onClearAll,
}: BacklogFiltersProps) {
  const hasActiveSort = sortBy !== 'number' || sortDirection !== WORK_ITEM_DEFAULT_SORT_DIRECTIONS.number
  const hasActiveFilters = search !== '' || selectedEpics.length > 0 || statuses.length > 0 || assignedTo.length > 0 || hasActiveSort
  const epicOptions = useMemo(
    () => [
      { value: NO_EPIC_FILTER_VALUE, label: 'No Epic' },
      ...epics.filter(Boolean).map((epic) => ({ value: epic, label: epic })),
    ],
    [epics],
  )

  return (
    <div className="flex items-center gap-4">
      <div className="relative flex-1 max-w-[652px]">
        <SearchIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-card-foreground" />
        <Input
          placeholder="The list is searchable by work item name, epic, #"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-9 pr-9 rounded-lg border border-border"
          aria-label="Search work items"
        />
        {search && (
          <button
            type="button"
            onClick={() => onSearchChange('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <MultiSelect
        options={epicOptions}
        value={selectedEpics}
        onChange={onEpicsChange}
        placeholder="All Epics"
        displayMode="compact"
        searchable={false}
        showCompactIcon={false}
        showCompactOverflowCount
        className="w-[200px]"
        aria-label="Filter by epic"
      />

      <MultiSelect
        options={workItemStatusFilterOptions}
        value={statuses}
        onChange={onStatusesChange}
        placeholder="All Statuses"
        displayMode="compact"
        searchable={false}
        showCompactIcon={false}
        showCompactOverflowCount
        className="w-[200px]"
        aria-label="Filter by status"
      />

      <MultiSelect
        options={assigneeOptions}
        value={assignedTo}
        onChange={onAssignedToChange}
        placeholder="Assigned To"
        searchPlaceholder="Search by name"
        displayMode="compact"
        showCompactIcon={false}
        showCompactOverflowCount
        className="w-[240px] rounded-lg border border-border"
        aria-label="Filter by assignee"
      />

      <div className="ml-auto flex items-center gap-4">
        <Button
          type="button"
          variant="ghost"
          onClick={onClearAll}
          disabled={!hasActiveFilters}
          className="flex items-center gap-1.5 text-sm font-medium"
          aria-label="Clear all filters"
        >
          <ClearFiltersIcon className="h-4 w-4" />
          Clear All
        </Button>

        <Button
          type="button"
          className={cn(
            'border border-border-accent',
            `${selectionMode ? 'bg-primary text-primary-foreground' : 'bg-toolbar'}`
          )}
          variant={selectionMode ? 'default' : 'outline'}
          size="icon"
          onClick={onToggleSelectionMode}
          aria-label="Toggle multi-select mode"
          aria-pressed={selectionMode}
          data-testid="backlog-selection-mode-toggle"
        >
          <BoxSelect className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
